/**
 * Sequential poller: schedules the next run only after the previous one has
 * finished, so a slow backend can never accumulate overlapping requests
 * (unlike setInterval).
 *
 * - `task` returns false to stop polling; anything else (or nothing) continues.
 * - If `task` throws, the delay backs off exponentially (up to `maxIntervalMs`)
 *   and polling stops after `maxConsecutiveFailures` failures in a row.
 * - Polling pauses while the tab is hidden and resumes (immediately) when it is
 *   visible again.
 *
 * @param {() => Promise<boolean|void>} task
 * @param {{ intervalMs: number, maxIntervalMs?: number, maxConsecutiveFailures?: number, onStop?: () => void }} options
 *   onStop is called when the poller stops by itself (task returned false, or too many failures)
 * @returns {{ start: () => void, stop: () => void, isActive: () => boolean }}
 */
export function createSequentialPoller(task, options) {
  const {
    intervalMs,
    maxIntervalMs = intervalMs * 6,
    maxConsecutiveFailures = 5,
    onStop = () => {},
  } = options;

  let active = false;
  let running = false; // a task execution is currently pending
  let timer = null;
  let failures = 0;

  const isHidden = () => typeof document !== 'undefined' && document.hidden === true;

  const clearTimer = () => {
    if (timer) { clearTimeout(timer); timer = null; }
  };

  const detach = () => {
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
  };

  const schedule = (delay) => {
    clearTimer();
    if (!active) return;
    timer = setTimeout(run, delay);
  };

  async function run() {
    timer = null;
    if (!active || running) return;
    if (isHidden()) return; // resumed by the visibilitychange handler
    running = true;
    let delay = intervalMs;
    try {
      const result = await task();
      failures = 0;
      if (result === false) { active = false; }
    } catch {
      failures += 1;
      if (failures >= maxConsecutiveFailures) {
        active = false;
      } else {
        delay = Math.min(maxIntervalMs, intervalMs * 2 ** failures);
      }
    } finally {
      running = false;
    }
    if (!active) { detach(); onStop(); return; }
    schedule(delay);
  }

  const onVisibilityChange = () => {
    if (active && !running && !isHidden()) {
      clearTimer();
      run();
    }
  };

  return {
    start() {
      if (active) return;
      active = true;
      failures = 0;
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onVisibilityChange);
      }
      run();
    },
    stop() {
      active = false;
      clearTimer();
      detach();
    },
    isActive: () => active,
  };
}
